<template>
  <div class="text-gray-900 h-full py-6 pl-6 pb-6 max-w-[1650px]">
    <nav
      class="fixed top-6 bottom-6 w-[240px] rounded-l-md basis-full overflow-x-scroll overflow-y-autogit shrink flex flex-col font-medium pb-10 z-10 bg-black text-white"
    >
      <div v-if="isMultiUser" class="border-b border-white w-full bg-[#2b2b2b] py-2">
        <InlineSelect
          v-model="customerId"
          class="mx-6 nav"
          :options="customers"
          optionValue="id"
          optionLabel="name"
          filter
          asNavigation
        />
      </div>
      <div v-else class="mt-6"></div>
      <div class="mb-6 mt-6 mx-6">
        <RouterLink :to="{ name: 'dashboard' }">
          <img src="/klimate-logo.svg" class="h-10 w-full" />
        </RouterLink>
      </div>
      <ul class="flex flex-col gap-2 ml-6 mr-15">
        <li class="text-xs text-gray-400">UNDERSTAND</li>
        <li>
          <RouterLink
            :to="{ name: 'dashboard' }"
            class="group flex items-center gap-3 active-link:underline hover:underline underline-offset-4"
          >
            <GlobeEuropeAfricaIcon class="size-6" />
            Dashboard
          </RouterLink>
        </li>
        <li>
          <RouterLink
            :to="{ name: 'projects' }"
            class="group flex items-center gap-3 active-link:underline hover:underline underline-offset-4"
          >
            <FolderIcon class="size-6" />
            Projects
          </RouterLink>
        </li>
        <li>
          <RouterLink
            :to="{ name: 'methods' }"
            class="group flex items-center gap-3 active-link:underline hover:underline underline-offset-4"
          >
            <BeakerIcon class="size-6" />
            Methods
          </RouterLink>
        </li>
        <li>
          <RouterLink
            :to="{ name: 'guides' }"
            class="group flex items-center gap-3 active-link:underline hover:underline underline-offset-4"
          >
            <InformationCircleIcon class="size-6" />
            Guides
          </RouterLink>
        </li>
        <li class="text-xs text-gray-400 mt-4">BUILD</li>
        <li>
          <RouterLink
            :to="{ name: 'strategy' }"
            class="group flex items-center gap-3 active-link:underline hover:underline underline-offset-4"
          >
            <MapIcon class="size-6" />
            Strategy
          </RouterLink>
        </li>
        <li>
          <RouterLink
            :to="{ name: 'new-order' }"
            class="group flex items-center gap-3 active-link:underline hover:underline underline-offset-4"
          >
            <PlusIcon class="size-6" />
            New order
          </RouterLink>
        </li>
        <li>
          <RouterLink
            :to="{ name: 'order-history' }"
            class="group flex items-center gap-3 active-link:underline hover:underline underline-offset-4"
          >
            <ShoppingCartIcon class="size-6" />
            Order history
          </RouterLink>
        </li>
        <li class="text-xs text-gray-400 mt-4">ORGANISE</li>
        <li>
          <RouterLink
            :to="{ name: 'impact-accounts' }"
            class="group flex items-center gap-3 active-link:underline hover:underline underline-offset-4"
          >
            <FolderOpenIcon class="size-6" />
            Impact accounts
          </RouterLink>
        </li>
        <li>
          <RouterLink
            :to="{ name: 'assets' }"
            class="group flex items-center gap-3 active-link:underline hover:underline underline-offset-4"
          >
            <ChartPieIcon class="size-6" />
            Assets
          </RouterLink>
        </li>
        <li>
          <RouterLink
            :to="{ name: 'import-assets' }"
            class="group flex items-center gap-3 active-link:underline hover:underline underline-offset-4"
          >
            <ChevronDoubleRightIcon class="size-6" />
            Import assets
          </RouterLink>
        </li>
        <li class="text-xs text-gray-400 mt-4">REPORT</li>
        <li>
          <RouterLink
            :to="{ name: 'reporting' }"
            class="group flex items-center gap-3 active-link:underline hover:underline underline-offset-4"
          >
            <PresentationChartBarIcon class="size-6" />
            Compliance
          </RouterLink>
        </li>
        <li>
          <RouterLink
            :to="{ name: 'communications' }"
            class="group flex items-center gap-3 active-link:underline hover:underline underline-offset-4"
          >
            <LifebuoyIcon class="size-6" />
            Communications
          </RouterLink>
        </li>
      </ul>

      <ul class="flex flex-col gap-2 ml-6 mt-auto pt-6">
        <li>
          <RouterLink
            :to="{ name: 'settings' }"
            class="group flex items-center gap-3 active-link:underline hover:underline underline-offset-4"
          >
            <Cog8ToothIcon class="size-6" />
            Account settings
          </RouterLink>
        </li>
        <li>
          <RouterLink
            :to="{ name: 'help' }"
            class="group flex items-center gap-3 active-link:underline hover:underline underline-offset-4"
          >
            <InformationCircleIcon class="size-6" />
            Help centre
          </RouterLink>
        </li>
        <li>
          <button
            class="flex items-center gap-3 hover:underline underline-offset-4"
            @click="emit('logout')"
          >
            <ArrowLeftStartOnRectangleIcon class="size-6" />
            Sign out
          </button>
        </li>
      </ul>
    </nav>

    <main class="ml-64 pr-8 h-full">
      <slot v-if="isCustomerSelectionReady"></slot>
    </main>
  </div>
</template>

<script setup lang="ts">
import { RouterLink } from 'vue-router'
import {
  ChartPieIcon,
  Cog8ToothIcon,
  ArrowLeftStartOnRectangleIcon,
  GlobeEuropeAfricaIcon,
  FolderOpenIcon,
  PresentationChartBarIcon,
  ShoppingCartIcon,
  InformationCircleIcon,
  LifebuoyIcon,
  FolderIcon,
  BeakerIcon,
  MapIcon,
  PlusIcon,
  ChevronDoubleRightIcon
} from '@heroicons/vue/24/outline'
import { graphql } from '@/gql'
import { request } from '@/graphql-client'
import { computed, ref, watch } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { isGod } from '@/auth0'
import { useCurrentUser } from '@/composables/useCurrentUser'
import InlineSelect from './Form/InlineSelect.vue'

const customerId = defineModel<number | undefined>()
const emit = defineEmits<{
  logout: []
}>()
const isCustomerSelectionReady = ref(false)
const { currentUser } = useCurrentUser()
const customers = computed(() => customersData.value?.customers ?? [])
const isMultiUser = computed(() => customers.value.length > 1)
const klimateCustomerId = Number(import.meta.env.VITE_DEFAULT_CLIENT_ID)

const { data: customersData } = useQuery({
  queryKey: ['customers'],
  queryFn: () =>
    request(
      graphql(`
        query GetCustomers {
          customers {
            id
            name
          }
        }
      `)
    )
})

// Resolve membership and accessible customer names before rendering company-specific pages.
// Admins can select customers outside their own membership for impersonation.
watch(
  [customersData, currentUser],
  ([customersData, user]) => {
    if (!customersData || !user) return

    const availableCustomers = customers.value
    const firstCustomer = availableCustomers[0]
    if (!firstCustomer) {
      isCustomerSelectionReady.value = true
      return
    }

    const hasAccessToSelectedCustomer = availableCustomers.some(
      (customer) => customer.id === customerId.value
    )
    if (!hasAccessToSelectedCustomer) {
      customerId.value = isGod() ? klimateCustomerId : firstCustomer.id
    }

    isCustomerSelectionReady.value = true
  },
  { immediate: true }
)
</script>
